import {
  forgotPasswordApiAuthForgotPasswordPost,
  getCurrentUserProfileApiAuthMeGet,
  inviteSessionApiAuthInviteSessionPost,
  refreshSessionApiAuthRefreshPost,
  resendConfirmationEmailApiAuthResendConfirmationPost,
  resetPasswordApiAuthResetPasswordPost,
  signInApiAuthSigninPost,
  signOutApiAuthSignoutPost,
  signUpApiAuthSignupPost,
  VerificationType,
  verifyEmailApiAuthVerifyEmailPost,
  type AuthResponse,
  type ForgotPasswordApiAuthForgotPasswordPostResponses,
  type PasswordResetRequest,
  type RefreshSessionApiAuthRefreshPostResponses,
  type ResendConfirmationEmailApiAuthResendConfirmationPostResponses,
  type ResetPasswordApiAuthResetPasswordPostResponses,
  type SignInApiAuthSigninPostResponses,
  type SignInRequest,
  type SignUpApiAuthSignupPostResponses,
  type SignUpRequest,
  type UserProfile,
  type VerifyEmailApiAuthVerifyEmailPostResponses,
} from '@/client';
import { createApiError } from '@/lib/api-error';
import { httpClient } from '@/lib/http-client';

export type User = UserProfile;
export type { AuthResponse };

type SignInSuccessResponse = SignInApiAuthSigninPostResponses[200];
type SignUpSuccessResponse = SignUpApiAuthSignupPostResponses[200];
type VerifyEmailSuccessResponse = VerifyEmailApiAuthVerifyEmailPostResponses[200];
type ResendConfirmationEmailSuccessResponse = ResendConfirmationEmailApiAuthResendConfirmationPostResponses[200];
type ForgotPasswordSuccessResponse = ForgotPasswordApiAuthForgotPasswordPostResponses[200];
type ResetPasswordSuccessResponse = ResetPasswordApiAuthResetPasswordPostResponses[200];

type SignUpResponseShape = {
  email: string;
  email_confirmation_required: boolean;
};

export type SignUpResponse = SignUpSuccessResponse & SignUpResponseShape;

type SignUpRequestWithInvite = SignUpRequest & {
  invite_token?: string | null;
};

export async function signIn(request: SignInRequest): Promise<AuthResponse> {
  const response = await signInApiAuthSigninPost({
    client: httpClient,
    body: request,
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return response.data as SignInSuccessResponse;
}

export async function signUp(request: SignUpRequestWithInvite): Promise<SignUpResponse | AuthResponse> {
  const response = await signUpApiAuthSignupPost({
    client: httpClient,
    body: request as SignUpRequest,
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return response.data as SignUpResponse | AuthResponse;
}

export async function inviteSession(invite_token: string): Promise<AuthResponse & { redirect?: string }> {
  const response = await inviteSessionApiAuthInviteSessionPost({
    client: httpClient,
    body: { invite_token },
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return response.data as AuthResponse & { redirect?: string };
}

export async function signOut(): Promise<void> {
  await signOutApiAuthSignoutPost({
    client: httpClient,
    headers: { authorization: '' }, // Handled by interceptor
    throwOnError: false,
  });
}
export async function getCurrentUser(): Promise<User | null> {
  const response = await getCurrentUserProfileApiAuthMeGet({
    client: httpClient,
    headers: { authorization: '' }, // Handled by interceptor
    throwOnError: false,
  });

  if (response.error) {
    return null;
  }

  return response.data as User;
}

export async function verifyEmailToken(token: string, type: VerificationType): Promise<AuthResponse | null> {
  const response = await verifyEmailApiAuthVerifyEmailPost({
    client: httpClient,
    body: { token, type },
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  const data = response.data as VerifyEmailSuccessResponse;
  if (data && typeof data === 'object' && 'access_token' in data) {
    return data as unknown as AuthResponse;
  }
  return null;
}

export async function resendConfirmationEmail(email: string): Promise<void> {
  const payload: PasswordResetRequest = { email };

  const response = await resendConfirmationEmailApiAuthResendConfirmationPost({
    client: httpClient,
    body: payload,
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  const data = response.data as ResendConfirmationEmailSuccessResponse;
  void data;
}

type RefreshSessionSuccessResponse = RefreshSessionApiAuthRefreshPostResponses[200];

export async function refreshToken(refresh_token: string): Promise<AuthResponse> {
  const response = await refreshSessionApiAuthRefreshPost({
    client: httpClient,
    body: { refresh_token },
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  return response.data as RefreshSessionSuccessResponse;
}

export async function forgotPassword(email: string): Promise<void> {
  const response = await forgotPasswordApiAuthForgotPasswordPost({
    client: httpClient,
    body: { email },
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  const data = response.data as ForgotPasswordSuccessResponse;
  void data;
}

export async function resetPassword(token: string, password: string): Promise<void> {
  const response = await resetPasswordApiAuthResetPasswordPost({
    client: httpClient,
    body: { token, password, type: 'recovery' },
    throwOnError: false,
  });

  if (response.error) {
    throw createApiError(response.error);
  }

  const data = response.data as ResetPasswordSuccessResponse;
  void data;
}